/* eslint-disable complexity */
/* eslint-disable react/jsx-no-target-blank */
import classNames from 'classnames/bind';
import { createRef } from 'preact';
import { useRef, useState } from 'preact/hooks';
import styles from './App.module.scss';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Notice } from './components/Notice';
import { Popup } from './components/Popup';
import { Spinner } from './components/Spinner';
import { RedeemApi } from './services/redeem-api';

const cx = classNames.bind(styles);

export function App() {
  const api = useRef(new RedeemApi());

  const [loading, setLoading] = useState(false);

  // 팝업
  const [popup, setPopup] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');

  // 폼을 한번 전송 시도 했는지 여부
  const [dirty, setDirty] = useState(false);

  const actionTypeRef = createRef<HTMLSelectElement>();
  const recommendCodeRef = createRef<HTMLInputElement>();
  const couponSerialRef = createRef<HTMLInputElement>();

  // # recommend_code, coupon_serial, action_type
  const [actionType, setActionType] = useState('');
  const [recommendCode, setRecommendCode] = useState('');
  const [couponSerial, setCouponSerial] = useState('');

  const resetPopup = () => {
    setPopup('');
  };

  const resetForm = () => {
    setActionType('');
    setRecommendCode('');
    setCouponSerial('');
    setDirty(false);
    resetPopup();
  };

  const showMessage = (message: string, title = '죄송합니다') => {
    setTitle(title);
    setMessage(message);
    setPopup('message');
  };

  const isValidActionType = /^(coupon|quiz)$/.test(actionType);

  const isValidRecommendCode =
    recommendCode.trim() && /^\d+$/.test(recommendCode);

  const isValidCouponSerial = Boolean(couponSerial.trim());

  const isEveryValid =
    isValidActionType && isValidRecommendCode && isValidCouponSerial;

  const claim = async () => {
    if (loading) return;

    setDirty(true);

    if (!isValidActionType) {
      setPopup('select-coupon');
      return;
    }

    if (!isValidRecommendCode) {
      setPopup('invalid-member');
      return;
    }

    if (!isValidCouponSerial) {
      setPopup('invalid-code');
      return;
    }

    setLoading(true);

    const { result, error } = await api.current.redeem({
      actionType,
      recommendCode,
      couponSerial,
    });

    setLoading(false);

    if (error && !result) {
      showMessage(
        '일시적인 오류가 발생하였습니다.<br/>문제가 계속되면 고객센터로 연락해주세요.'
      );
      return;
    }

    switch (result?.result_code) {
      case 2:
      case 1: {
        setPopup('success'); // 메시지에서 지급 방식 표시하지 않음
        break;
      }

      case 0: {
        showMessage('등록이 실패하였습니다.<br/>잠시 후 다시 시도해주세요.');
        break;
      }

      case -1: {
        setPopup('invalid-code'); // 쿠폰 번호 오류
        break;
      }

      case -4: {
        showMessage('계정당 쿠폰 사용 횟수를 초과하였습니다.', '안내');
        break;
      }

      case -6: {
        showMessage(
          '사용기간이 아닙니다.<br/>사용기간을 다시 확인해주세요.',
          '안내'
        );
        break;
      }

      case -9: {
        showMessage('이미 사용되었습니다.', '안내');
        break;
      }

      case -10: {
        showMessage(
          '이번 달 구매 한도가 초과되어<br/>쿠폰을 사용할 수 없습니다.',
          '안내'
        );
        break;
      }

      case -11: {
        setPopup('invalid-code');
        break;
      }

      case -14: {
        showMessage('이미 사용되었습니다.', '안내');
        break;
      }

      case -16: {
        showMessage(
          '사용기간이 아닙니다.<br/>사용기간을 다시 확인해주세요.',
          '안내'
        ); // 이벤트 기간 종료
        break;
      }

      case -20: {
        showMessage(
          '설정한 월 구매금액을 초과하여<br/>쿠폰을 사용할 수 없습니다.<p><b>* 월 구매금액 변경은 내정보 &gt; 셀프케어에서 가능합니다.</b></p>',
          '안내'
        ); // 셀프케어 설정 구매한도 초과
        break;
      }

      case -99: {
        showMessage(
          '등록이 실패하였습니다.<br/>고객센터로 문의해주세요.',
          '안내'
        ); // 카카오와 본인인증 미인증 유저
        break;
      }

      default: {
        showMessage(
          `일시적인 오류가 발생하였습니다.<br/>오류 코드 : ${
            result?.result_code ?? '없음'
          }<br/>요청 ID : ${result?.requestId ?? '없음'}`
        );
      }
    }
  };

  return (
    <div class={cx('app')}>
      <ErrorBoundary>
        <div class={cx('container')}>
          <div class={cx('logo')}>
            <h1>뉴맞고</h1>
          </div>
          <div class={cx('wrapper')}>
            <h2 class={cx('title')}>쿠폰 사용</h2>

            <div class={cx('field')}>
              <h3 class={cx('label')}>쿠폰 종류</h3>
              <select
                ref={actionTypeRef}
                class={cx('coupon-type', {
                  dirty: actionType,
                  error: dirty && !isValidActionType,
                })}
                placeholder="쿠폰 종류를 선택하세요"
                value={actionType}
                onChange={(event) => {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                  setActionType((event as any).target.value);
                }}
              >
                <option value="">쿠폰 종류를 선택하세요</option>
                <option value="coupon">쿠폰 등록</option>
                <option value="quiz">퀴즈 이벤트</option>
              </select>
            </div>

            <div class={cx('field')}>
              <h3 class={cx('label')}>회원 코드</h3>
              <input
                ref={recommendCodeRef}
                class={cx('input', 'member-code', {
                  error: dirty && !isValidRecommendCode,
                })}
                type="text"
                placeholder="회원 코드를 입력해주세요"
                value={recommendCode}
                onInput={(event) => {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                  setRecommendCode((event as any).target.value);
                }}
              />
            </div>

            <div class={cx('field')}>
              <h3 class={cx('label')}>
                쿠폰 번호<span style={{ margin: '0 -4px' }}>˙</span>정답
              </h3>
              <input
                ref={couponSerialRef}
                class={cx('input', 'coupon-code', {
                  error: dirty && !isValidCouponSerial,
                })}
                type="text"
                placeholder="쿠폰 번호 또는 정답을 입력해주세요"
                value={couponSerial}
                onInput={(event) => {
                  const value = (event as any).target.value as string;
                  setCouponSerial(
                    // 쿠폰일 경우 대문자로, 퀴즈일 경우 변경하지 않음
                    actionType === 'coupon' ? value.toUpperCase() : value
                  );
                }}
              />
            </div>

            <button
              class={cx('confirm', {
                valid: isEveryValid,
              })}
              onClick={(event) => {
                claim().catch((error_) => {
                  console.log(error_);
                });
              }}
            >
              확인하기
            </button>

            <div class={cx('notice')}>
              <Notice />
            </div>
          </div>
        </div>

        <footer class={cx('footer')}>
          <a
            href="https://neowiz.com/neowiz"
            target="_blank"
            class={cx('neowiz')}
          >
            NEOWIZ
          </a>
          <span>&copy; NEOWIZ All rights reserved.</span>
        </footer>

        {popup === 'select-coupon' && (
          <Popup
            title="안내"
            message="쿠폰 종류를 선택하세요."
            onConfirm={() => {
              resetPopup();
              actionTypeRef.current!.focus();
            }}
          />
        )}

        {popup === 'invalid-member' && (
          <Popup
            title="안내"
            message="회원코드를 정확히 입력해주세요."
            onConfirm={() => {
              resetPopup();
              recommendCodeRef.current!.select();
              recommendCodeRef.current!.focus();
            }}
          />
        )}

        {popup === 'invalid-code' && (
          <Popup
            title="안내"
            message="쿠폰번호/정답을<br/>정확히 입력해주세요."
            onConfirm={() => {
              resetPopup();
              couponSerialRef.current!.select();
              couponSerialRef.current!.focus();
            }}
          />
        )}

        {popup === 'success' && (
          <Popup
            title="안내"
            message="정상적으로 지급되었습니다."
            onConfirm={resetForm}
          />
        )}

        {popup === 'failed' && (
          <Popup
            title="안내"
            message="등록이 실패하였습니다.<br/>다시 시도해주세요."
            onConfirm={resetForm}
          />
        )}

        {popup === 'message' && (
          <Popup title={title} message={message} onConfirm={resetForm} />
        )}
      </ErrorBoundary>

      {/* 빙글빙글 */}
      <Spinner visible={loading} />
    </div>
  );
}
